// Pure helpers used by the team_assist_proposed_action component. Lives in
// its own module so unit tests can exercise them without pulling in the React
// component tree (and its transitive imports — Button, FontAwesome icons,
// etc., which require a DOM-ish environment).

// extractSchemaProperties returns an ordered list of editable field
// descriptors from the destructive tool's JSON Schema. Unrecognised types
// fall back to a single-line text input. Returns [] when no schema is
// present so callers can fall back to the raw-args preview.
export function extractSchemaProperties(inputSchema) {
  if (!inputSchema || typeof inputSchema !== 'object') {
    return [];
  }
  const properties = inputSchema.properties;
  if (!properties || typeof properties !== 'object') {
    return [];
  }
  const requiredSet = new Set(Array.isArray(inputSchema.required) ? inputSchema.required : []);
  return Object.keys(properties).map(name => {
    const spec = properties[name] || {};
    return {
      name,
      label: spec.title || name,
      description: spec.description || '',
      type: inferFieldType(spec),
      enumValues: Array.isArray(spec.enum) ? spec.enum : null,
      required: requiredSet.has(name),
    };
  });
}

export function inferFieldType(spec) {
  if (Array.isArray(spec.enum) && spec.enum.length > 0) {
    return 'enum';
  }
  switch (spec.type) {
    case 'integer':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'string':
      // The backend only emits the `date-time` JSON Schema format, never
      // `date`, so any other format (including `date`) falls back to text.
      if (spec.format === 'date-time') {
        return 'date';
      }
      return 'text';
    default:
      return 'text';
  }
}

// initialFormValuesFor prefills the form from the LLM's proposed Arguments
// blob (object or JSON-string). Only keys declared in the schema are kept —
// downstream the form value object becomes the executed arguments, and the
// schema is the contract. Date fields are seeded in the same datetime-local
// display form the control renders (see iso8601ToDatetimeLocal), so a value
// the agent can't see — whitespace, a non-string, or an unparseable date —
// collapses to "" in state rather than lingering as a hidden payload that
// findMissingRequired would treat as present and Execute would send unchanged.
export function initialFormValuesFor(args, schemaProperties) {
  const parsed = parseProposedActionArguments(args);
  const initial = {};
  schemaProperties.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(parsed, field.name)) {
      const value = parsed[field.name];
      initial[field.name] = field.type === 'date' ? iso8601ToDatetimeLocal(value) : value;
    } else {
      initial[field.name] = field.type === 'boolean' ? false : '';
    }
  });
  return initial;
}

export function parseProposedActionArguments(args) {
  if (args == null) {
    return {};
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(args);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  if (typeof args === 'object') {
    return args;
  }
  return {};
}

// datetimeLocalToIso8601 converts an <input type="datetime-local"> value
// (local wall-clock with no seconds or zone, e.g. "2026-06-11T15:01") into a
// full ISO 8601 instant in UTC ("2026-06-11T22:01:00Z"). The backend tool
// schema's `date-time` format requires seconds and a timezone, neither of
// which the native control emits; the zone is the agent's browser-local zone,
// matching how the rest of the app derives timezone (Intl / moment.tz.guess).
// Fractional seconds are deliberately dropped — the backend's RFC 3339
// validator expects the second-precision form (e.g. "2026-06-12T14:00:00Z"),
// not the millisecond form Date.toISOString() emits by default.
// Returns the input unchanged when it is empty or not parseable so we never
// silently drop what the agent entered.
export function datetimeLocalToIso8601(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// iso8601ToDatetimeLocal converts a full ISO 8601 instant (the wire format the
// LLM prefills date-time arguments in) into the local-time "YYYY-MM-DDThh:mm"
// string the native datetime-local control requires to display a value.
// Returns "" for anything the control can't render — empty, whitespace,
// non-string, or an unparseable date — mirroring what the control actually
// shows for such a value (nothing). This is also the canonical "what the field
// displays" form used to seed form state, so validation and submission operate
// on the same value the agent sees; re-rendering a value already in the bare
// datetime-local shape is a no-op round-trip.
export function iso8601ToDatetimeLocal(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const pad = n => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

// normalizeFormValuesForSubmit converts editable field values into the wire
// representation the backend expects before they become the executed
// arguments. Only date-time fields need adjustment today: their datetime-local
// input value is promoted to a full ISO 8601 instant.
export function normalizeFormValuesForSubmit(formValues, schemaProperties) {
  const normalized = { ...formValues };
  schemaProperties.forEach(field => {
    if (field.type === 'date') {
      normalized[field.name] = datetimeLocalToIso8601(normalized[field.name]);
    }
  });
  return normalized;
}

export function findMissingRequired(formValues, schemaProperties) {
  return schemaProperties
    .filter(field => field.required)
    .filter(field => {
      const value = formValues[field.name];
      if (value == null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    })
    .map(field => field.name);
}

// formatToolDisplayName renders a destructive external tool name as
// `<action> (<config>)`, where <action> is the substring after the last '-'
// and <config> is the substring before the first '-'. The MCP tool naming
// convention is `<configName>-<source>-<actionName>`, so this surfaces the
// agent-meaningful action name with the configuration that owns it in
// parentheses. Falls back to the raw name when it has no '-' or when either
// side would be empty.
export function formatToolDisplayName(toolName) {
  if (!toolName) {
    return '';
  }
  if (!toolName.includes('-')) {
    return toolName;
  }
  const lastDash = toolName.lastIndexOf('-');
  const firstDash = toolName.indexOf('-');
  const action = toolName.slice(lastDash + 1);
  const config = toolName.slice(0, firstDash);
  if (!action || !config) {
    return toolName;
  }
  return `${action} (${config})`;
}

// formatProposedActionArguments renders the LLM-emitted argument blob as
// pretty-printed JSON text. Always returns text — never HTML — so the
// caller can safely place it inside a <pre> without dangerouslySetInnerHTML.
// Accepts either a plain object or a JSON string (the wire format from
// supernova).
export function formatProposedActionArguments(args) {
  if (args == null) {
    return '';
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch (e) {
      return args;
    }
  }
  try {
    return JSON.stringify(args, null, 2);
  } catch (e) {
    return String(args);
  }
}
